export type CompetitionSource = {
  source: string;
  kind: string;
  name: string;
  home: string | null;
  topic: string | null;
  ours: boolean;
  keywords: number;
  keywordList: string[];
  appearances: number;
  top3: number;
  avgRank: number | null;
  bestRank: number;
  lastDay: string;
};
export type CompetitionSources = {
  days: number;
  top: number;
  area: string;
  keywords: number;
  collectedDays: number;
  items: CompetitionSource[];
};

export type PageMetrics = {
  chars: number;
  paragraphs: number;
  images: number;
  videos: number;
  tables: number;
  links: number;
  maps: number;
  headings: number;
  quotes: number;
  tags: string[];
  comments: number | null;
  editor: "se" | "html";
};
export type PageStatus =
  | "pending"
  | "ok"
  | "failed"
  | "blocked"
  | "unsupported"
  | "gone";
export type CompetitionItem = {
  rank: number;
  title: string;
  link: string;
  source: string;
  name: string;
  home: string | null;
  date: string;
  desc: string | null;
  our: boolean;
  page: {
    status: PageStatus;
    error: string | null;
    fetchedAt: string | null;
    title: string | null;
    publishedAt: string | null;
    ageDays: number | null;
    topic: string | null;
    metrics: PageMetrics | null;
    keyword: {
      inTitle: boolean;
      inFirst: boolean;
      count: number;
      per1k: number;
    } | null;
  } | null;
};
export type Benchmark = {
  key: string;
  label: string;
  unit: string;
  ratio: boolean;
  top3: number | null;
  top10: number | null;
  samples: number;
  values: { rank: number; value: number | null }[];
  ours: number | null;
  diff: number | null;
  direction: "higher" | "lower" | null;
};
export type Change = {
  rank: number | null;
  title: string;
  link: string;
  name: string;
  our: boolean;
  previousRank: number | null;
};
export type CompetitionView = {
  keyword: string;
  area: string;
  collectedAt: string | null;
  total: number | null;
  depth: number | null;
  ourBestRank?: number | null;
  items: CompetitionItem[];
  benchmark: Benchmark[];
  pending: number;
  history?: {
    labels: string[];
    series: {
      source: string;
      name: string;
      our: boolean;
      values: (number | null)[];
    }[];
  };
  changes?: { previousAt: string; entered: Change[]; left: Change[] } | null;
};
export type ProbeView = CompetitionView & {
  id: string;
  areas: string[];
  error: string | null;
  createdAt: string;
};
export type ProbeRow = {
  id: string;
  keyword: string;
  areas: string[];
  createdAt: string;
  error: string | null;
  createdByName: string | null;
  totals: Record<string, number> | null;
};

// Design fixtures only. Shapes mirror apps/console/src/lib/competition-api.ts.
// No competitor body text, credentials or actual network collection.
export type CompetitionExample =
  | "normal"
  | "outside"
  | "day-one"
  | "empty"
  | "first"
  | "pending"
  | "statuses"
  | "free-result"
  | "free-pending"
  | "free-failed"
  | "free-no-key";
export const competitionAreas: Record<string, string> = {
  blog: "블로그",
  web: "웹",
  news: "뉴스",
  cafe: "카페",
};
export const metricDefinitions = [
  ["chars", "글자", "자", false],
  ["images", "사진", "개", false],
  ["headings", "소제목", "개", false],
  ["kwCount", "키워드 횟수", "회", false],
  ["kwTitle", "제목 키워드", "%", true],
  ["ageDays", "글 나이", "일", false],
  ["videos", "영상", "개", false],
  ["tables", "표", "개", false],
  ["links", "링크 카드", "개", false],
  ["maps", "지도", "개", false],
  ["tags", "태그", "개", false],
  ["comments", "댓글", "개", false],
  ["kwFirst", "첫 문단 키워드", "%", true],
  ["kwPer1k", "1천 자당 키워드", "회", false],
] as const;
export const competitionWords = (customer: string) =>
  customer === "moru"
    ? ["성수 맛집", "성수 점심", "서울숲 식당"]
    : ["강남 내과", "강남 건강검진", "역삼 내과"];
const median = (values: number[]) => {
  const a = [...values].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2);
  return a.length ? (a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2) : null;
};
const valueOf = (item: CompetitionItem, key: string): number | null => {
  key =
    (
      {
        kwCount: "count",
        kwTitle: "inTitle",
        kwFirst: "inFirst",
        kwPer1k: "per1k",
      } as Record<string, string>
    )[key] ?? key;
  const p = item.page;
  if (p?.status !== "ok" || !p.metrics || !p.keyword) return null;
  if (key === "inTitle" || key === "inFirst") return p.keyword[key] ? 100 : 0;
  if (key === "count" || key === "per1k") return p.keyword[key];
  if (key === "ageDays") return p.ageDays;
  if (key === "tags") return p.metrics.tags.length;
  return p.metrics[key as keyof PageMetrics] as number | null;
};
export function competitionFixture(
  customer: string,
  area: string,
  days: number,
  keyword: string,
  example: CompetitionExample = "normal",
  free = false,
): CompetitionView {
  const outside = example === "outside",
    first = example === "first",
    pending = example === "pending" || example === "free-pending";
  const rank = outside ? 34 : 7;
  const names = [
    "건강한 하루",
    "도시생활 기록",
    "생활 안내 노트",
    "일상 연구소",
    "동네 이야기",
    "오늘의 기록",
    customer === "moru" ? "모루식당" : "온유의원",
    "알아두는 정보",
    "지역 소식",
    "산책 일기",
  ];
  const items: CompetitionItem[] = Array.from(
    { length: outside && !free ? 11 : 10 },
    (_, i) => {
      const our = !free && i === (outside ? 10 : 6),
        r = our ? rank : i + 1;
      const status: PageStatus =
        area === "cafe"
          ? "unsupported"
          : pending
            ? i < 2 && example === "free-pending"
              ? "ok"
              : "pending"
            : example === "statuses"
              ? ([
                  "ok",
                  "pending",
                  "blocked",
                  "unsupported",
                  "gone",
                  "failed",
                  "ok",
                  "ok",
                  "ok",
                  "ok",
                ][i] as PageStatus)
              : "ok";
      const chars = [
        1840, 2260, 1540, 3150, 2180, 1420, 1280, 2640, 1890, 2050, 1280,
      ][i];
      return {
        rank: r,
        title:
          keyword +
          (our
            ? " · 방문 전 알아두면 좋은 정보"
            : " · " +
              [
                "처음 찾는 분을 위한 안내",
                "선택 전에 확인할 항목",
                "방문 후기와 이용 정보",
              ][i % 3]),
        link: "https://example.com/article/" + r,
        source: "blog:sample-" + i,
        name: our ? (customer === "moru" ? "모루식당" : "온유의원") : names[i],
        home: null,
        date: "2026-10-" + String(19 - i).padStart(2, "0"),
        desc: null,
        our,
        page: {
          status,
          error: status === "failed" ? "응답 시간 초과" : null,
          fetchedAt: status === "pending" ? null : "2026-10-20T00:00:00Z",
          title: null,
          publishedAt:
            "2026-10-" + String(19 - i).padStart(2, "0") + "T00:00:00Z",
          ageDays: i + 1,
          topic:
            area === "news"
              ? "지역 소식"
              : customer === "moru"
                ? "맛집 · 음식"
                : "건강 · 생활",
          metrics:
            status === "ok"
              ? {
                  chars,
                  paragraphs: 12 + i,
                  images: [8, 12, 6, 10, 9, 5, 4, 11, 7, 8, 4][i],
                  videos: i % 3 === 0 ? 1 : 0,
                  tables: i % 2,
                  links: 2,
                  maps: 1,
                  headings: [5, 6, 4, 8, 5, 3, 2, 7, 4, 5, 2][i],
                  quotes: 1,
                  tags: ["지역", "정보", ...(i % 2 ? ["방문"] : [])],
                  comments: i === 3 ? null : i * 2,
                  editor: "se",
                }
              : null,
          keyword:
            status === "ok"
              ? {
                  inTitle: i !== 5,
                  inFirst: i % 3 !== 0,
                  count: [7, 9, 5, 12, 6, 4, 3, 10, 8, 6, 3][i],
                  per1k:
                    Math.round(
                      ([7, 9, 5, 12, 6, 4, 3, 10, 8, 6, 3][i] / chars) * 10000,
                    ) / 10,
                }
              : null,
        },
      };
    },
  );
  const benchmarks: Benchmark[] = metricDefinitions.map(
    ([key, label, unit, ratio]) => {
      const values = items
        .filter((i) => i.rank <= 10)
        .map((i) => ({ rank: i.rank, value: valueOf(i, key) }));
      const nums = values.flatMap((v) => (v.value == null ? [] : [v.value])),
        top = values
          .filter((v) => v.rank <= 3)
          .flatMap((v) => (v.value == null ? [] : [v.value]));
      const agg = (a: number[]) =>
        ratio
          ? a.length
            ? a.reduce((s, n) => s + n, 0) / a.length
            : null
          : median(a);
      const own = items.find((i) => i.our),
        ours = own ? valueOf(own, key) : null,
        top10 = agg(nums);
      return {
        key,
        label,
        unit,
        ratio,
        values,
        top3: agg(top),
        top10,
        samples: nums.length,
        ours,
        diff: ours == null || top10 == null ? null : ours - top10,
        direction:
          ours == null || top10 == null || ours === top10
            ? null
            : ours < top10
              ? "higher"
              : "lower",
      };
    },
  );
  const labels = Array.from(
    { length: example === "day-one" ? 1 : Math.min(days, 7) },
    (_, i) =>
      "10." +
      String(20 - (example === "day-one" ? 0 : Math.min(days, 7) - 1) + i),
  );
  return {
    keyword,
    area,
    collectedAt: first ? null : "2026-10-20T00:00:00Z",
    total: first ? null : 18420,
    depth: first ? null : 100,
    ourBestRank: free ? undefined : first ? null : rank,
    items: first ? [] : items,
    benchmark: first ? [] : benchmarks,
    pending: items.filter((i) => i.page?.status === "pending").length,
    ...(!free
      ? {
          history: {
            labels,
            series: [...names.slice(0, 6), ...names.slice(7), "우리"].map(
              (name, j) => ({
                source: "source-" + j,
                name,
                our: j === 9,
                values: labels.map((_, i) =>
                  j === 9
                    ? outside
                      ? 34
                      : 7
                    : Math.min(10, 1 + j + (i % 3 === 0 ? 1 : 0)),
                ),
              }),
            ),
          },
          changes:
            example === "day-one" || first
              ? null
              : {
                  previousAt: "2026-10-19T00:00:00Z",
                  entered: [
                    {
                      rank: 3,
                      previousRank: 14,
                      title: keyword + " · 방문 후기",
                      link: "https://example.com/new",
                      name: "생활 안내 노트",
                      our: false,
                    },
                  ],
                  left: [
                    {
                      rank: 15,
                      previousRank: 8,
                      title: keyword + " · 이용 정보",
                      link: "https://example.com/left",
                      name: "지역 생활",
                      our: false,
                    },
                  ],
                },
        }
      : {}),
  };
}
export function sourceFixture(
  customer: string,
  area: string,
  days: number,
  example: CompetitionExample,
): CompetitionSources {
  const collectedDays =
    example === "day-one"
      ? 1
      : example === "first" || example === "empty"
        ? 0
        : Math.min(days, 12);
  const words = competitionWords(customer);
  return {
    days,
    top: 10,
    area,
    keywords: example === "empty" ? 0 : words.length,
    collectedDays,
    items: collectedDays
      ? Array.from({ length: 17 }, (_, i) => ({
          source: "sample:" + i,
          kind:
            area === "cafe"
              ? "naver_cafe"
              : area === "blog"
                ? "naver_blog"
                : "web",
          name:
            i === 4
              ? customer === "moru"
                ? "모루식당"
                : "온유의원"
              : [
                  "건강한 하루",
                  "도시생활 기록",
                  "생활 안내 노트",
                  "일상 연구소",
                  "우리",
                  "동네 이야기",
                ][i % 6] + (i > 5 ? " " + (i + 1) : ""),
          home: null,
          topic: customer === "moru" ? "맛집 · 음식" : "건강 · 생활",
          ours: i === 4,
          keywords: i < 5 ? 3 : 2,
          keywordList: i < 5 ? words : words.slice(0, 2),
          appearances: Math.max(
            1,
            Math.round((1 - i / 20) * collectedDays * 3),
          ),
          top3: Math.max(0, Math.round((1 - i / 18) * collectedDays)),
          avgRank: Math.min(10, 2.1 + i * 0.4),
          bestRank: i < 5 ? 1 : 3,
          lastDay: "2026-10-20",
        }))
      : [],
  };
}
